<script setup>
  import { getActivePinia } from 'pinia'
  import { useRoute } from 'vue-router'

  import { onBeforeMount, onUnmounted, ref } from 'vue'

  import { useAuthStore } from '@/features/auth/useAuthStore.js'
  import { useClubRegisterLastStep } from '@/features/clubRegister/utils/useClubRegisterLastStep.js'
  import {
    clubRegisterStepRouteMap,
    useClubRegisterStore,
  } from '@/features/clubRegister/views/useClubRegisterStore.js'
  import { useDevModeStore } from '@/features/devMode/useDevModeStore.js'
  import { getActiveClubId, getIsImpersonation } from '@/features/router/useActiveClub'
  import { useClubRouter } from '@/features/router/useClubRouer.js'
  import { isSuperAdmin } from '@/features/superAdmin/useIsSuperAdmin'
  import Spinner from '@/features/ui/components/Spinner.vue'

  const clubRegisterStore = useClubRegisterStore()

  const authStore = useAuthStore()
  async function handleSelectStep(event) {
    await authStore.getMyDetailsWithoutCache(false)

    const step = event.target.value
    clubRegisterStore.setCurrentStep(step, clubRegisterStore.clubId)
  }

  const devModeStore = useDevModeStore()

  const route = useRoute()
  const router = useClubRouter()
  const clubId = route.query.clubId

  const { getLastStep } = useClubRegisterLastStep()
  const isLoading = ref(true)

  onBeforeMount(async function clubRegisterView() {
    try {
      if (!authStore.isLoggedIn) {
        return
      }

      const myDetailsResponse = await authStore.getMyDetailsWithoutCache(false)

      if (clubId && clubRegisterStore.currentStep === clubRegisterStore.STEPS.LOGIN) {
        const lastStep = await getLastStep(clubId)
        if (lastStep) {
          console.log('Last step found', lastStep)
          clubRegisterStore.setCurrentStep(lastStep, clubId)
        } else {
          clubRegisterStore.setCurrentStep(clubRegisterStore.STEPS.ORG_DETAILS, clubId)
        }
      }

      if (!clubId) {
        if (isSuperAdmin(myDetailsResponse) && getIsImpersonation(myDetailsResponse)) {
          console.log('redirect to impersonated club')
          const activeClubId = getActiveClubId()
          const lastStep = await getLastStep(activeClubId)
          await router.push(
            `/club-register/${clubRegisterStepRouteMap[lastStep || clubRegisterStore.STEPS.ORG_DETAILS]}`,
            {
              query: {
                clubId: activeClubId,
              },
            },
          )
          return
        }

        const firstNotRegistered = (myDetailsResponse?.myTenants || []).find(item => {
          return !item.isRegisteredFinalized
        })

        console.timeLog('firstNotRegistered', firstNotRegistered)

        const firstNotRegisteredTenantId = firstNotRegistered?.tenantId

        if (firstNotRegisteredTenantId) {
          console.log('redirect to first clubs details', firstNotRegisteredTenantId)

          const lastStep = await getLastStep(firstNotRegisteredTenantId)

          await router.push({
            path: `/club-register/${clubRegisterStepRouteMap[lastStep || clubRegisterStore.STEPS.ORG_DETAILS]}`,
            query: {
              clubId: firstNotRegisteredTenantId,
            },
          })
        } else {
          console.log('nothing to redirect')
        }
      }
    } finally {
      isLoading.value = false
    }
  })

  onUnmounted(() => {
    const pinia = getActivePinia()

    pinia.state.value.clubRegisterDetailsStore = {}
    pinia.state.value.clubRegisterOrgTypeStore = {}
    pinia.state.value.clubRegisterBackgroundStore = {}
    pinia.state.value.clubRegisterBrandingStore = {}
    pinia.state.value.clubRegisterOrgStructureStore = {}
    pinia.state.value.clubRegisterComponentsStore = {}
    pinia.state.value.clubRegisterPublicContactStore = {}
    pinia.state.value.clubRegisterTerminologyStore = {}
  })
</script>

<template>
  <select v-if="devModeStore.isDevMode" class="dev-select" @change="handleSelectStep">
    <option v-for="step of clubRegisterStore.STEPS" :key="step" :value="step">
      {{ step }}
    </option>
  </select>

  <Spinner v-if="isLoading || authStore.myDetailsLoading" />
  <RouterView v-else />
</template>

<style lang="scss" scoped>
  .dev-select {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 1000;
    display: none;

    &.show {
      display: block;
    }
  }
</style>
